import { computed, nextTick, reactive, ref, type ComputedRef } from 'vue';

import {
  findError,
  type ErrorKeysType,
} from '@magma-app/magma-errors/dist/index';

import AnalyticsHelper from '@/helpers/analytics';
import { isPhoneRateLimited } from '@/composables/useResendTimer';
import { localizeErrorMessage } from '@/services/http';
import type { useAuthStore } from '@/stores/auth';
import {
  buildCriteriaPayload,
  initializeCriteriaAnswers,
} from '@/composables/helpee-signup/useHelpeeCriteria';
import type {
  CampaignCriteria,
  CriteriaAnswers,
} from '@/composables/helpee-signup/useHelpeeCriteria';

// Errors caused by the number itself, which the prospect fixes by changing it
// (service outages and rate limits stay toast-only)
const PHONE_NUMBER_ERROR_SUBTYPES = [
  'ERROR-BLOCKED-COUNTRY',
  'ERROR-NON-SMS-LINE-TYPE',
  'ERROR-INVALID-PHONE-NUMBER',
  'INVALID-PHONE-NUMBER',
  'ERROR-TEMPORARY-PHONE-NUMBER',
];

type AuthStore = ReturnType<typeof useAuthStore>;

type RouteLike = {
  query: Record<string, unknown>;
  params: Record<string, unknown>;
};

type StepsController = {
  currentStep: {
    value: 'intro' | 'profile' | 'phone' | 'criteria' | 'redirect';
  };
  goToStep: (
    step: 'intro' | 'profile' | 'phone' | 'criteria' | 'redirect'
  ) => void;
  isIntro: ComputedRef<boolean>;
  isPhone: ComputedRef<boolean>;
  isCriteria: ComputedRef<boolean>;
  isRedirect: ComputedRef<boolean>;
};

type PhoneVerificationController = {
  codeError: { value: boolean };
  timerBeforeResend: { value: number };
  canResend: { value: boolean };
  isResending: { value: boolean };
  startTimer: () => void;
  resendCode: (sendFn: () => Promise<void>) => Promise<void>;
  verifyCode: (validateFn: () => Promise<boolean>) => Promise<boolean>;
  clearError: () => void;
};

export function useHelpeeSignupFlow(params: {
  authStore: AuthStore;
  route: RouteLike;
  steps: StepsController;
  phoneVerification: PhoneVerificationController;
  strongCriteria: ComputedRef<CampaignCriteria | undefined>;
  optionalCriteria: ComputedRef<CampaignCriteria[]>;
}) {
  const isLoading = ref(false);
  // Shown under the phone field when the backend rejects the number itself
  // (the toast alone disappears after 3s and is easy to miss on mobile)
  const phoneError = ref<string | null>(null);
  const clearPhoneError = () => {
    phoneError.value = null;
  };
  const lastPhoneVerificationNumber = ref<string | null>(null);

  const profileForm = reactive({
    firstname: params.authStore.helpeePostSignUpData?.firstname || '',
    lastname: params.authStore.helpeePostSignUpData?.lastname || '',
    phoneNumber: params.authStore.helpeePostSignUpData?.phoneNumber || '',
    criteria: null as string | string[] | null,
  });

  const verificationCode = ref('');
  const optionalCriteriaAnswers = ref<CriteriaAnswers>({});

  const phoneRequired = computed(
    () =>
      params.authStore.helpeePostMatchData?.needPhoneNumberVerification === true
  );

  const hasOptionalSelection = computed(() => {
    const criteriaList = params.optionalCriteria.value;
    if (criteriaList.length === 0) {
      return true;
    }

    return criteriaList.every((criteria) => {
      const answer = optionalCriteriaAnswers.value[String(criteria.id)];
      if (Array.isArray(answer)) {
        return answer.length > 0;
      }
      return Boolean(answer);
    });
  });

  const handleIntroSuccess = async (data: Record<string, unknown>) => {
    if (typeof data.firstname === 'string') {
      profileForm.firstname = data.firstname;
    }

    if (typeof data.lastname === 'string') {
      profileForm.lastname = data.lastname;
    }

    if (typeof data.phoneNumber === 'string') {
      profileForm.phoneNumber = data.phoneNumber;
    }

    params.steps.goToStep('profile');
  };

  const handleProfileSubmit = async (payload: {
    firstname: string;
    lastname: string;
    phoneNumber: string;
    criteria: string | string[] | null;
  }) => {
    if (isPhoneRateLimited()) {
      phoneError.value = localizeErrorMessage(findError('GENERIC' as ErrorKeysType, 'TOO-MANY-REQUESTS')?.message) || null;
      return;
    }
    const prospectUuid = params.authStore.helpeePostSignUpData?.prospectUuid;
    if (
      !params.strongCriteria.value ||
      payload.criteria === null ||
      !prospectUuid
    ) {
      return;
    }

    profileForm.firstname = payload.firstname;
    profileForm.lastname = payload.lastname;
    profileForm.phoneNumber = payload.phoneNumber;
    profileForm.criteria = payload.criteria;

    const shouldReuseCurrentPhoneVerification =
      phoneRequired.value
      && lastPhoneVerificationNumber.value === payload.phoneNumber
      && !params.phoneVerification.canResend.value;

    if (shouldReuseCurrentPhoneVerification) {
      params.phoneVerification.clearError();
      params.steps.goToStep('phone');
      return;
    }

    isLoading.value = true;
    clearPhoneError();

    try {
      const objectKey = Array.isArray(payload.criteria)
        ? 'answerKeys'
        : 'answerKey';

      await params.authStore.completeSignUpHelpee({
        prospectUuid,
        firstname: payload.firstname,
        lastname: payload.lastname,
        phoneNumber: payload.phoneNumber,
        strongProspectCriteria: {
          criteriaId: params.strongCriteria.value.id,
          [objectKey]: payload.criteria,
        },
      });

      optionalCriteriaAnswers.value = initializeCriteriaAnswers(
        params.optionalCriteria.value
      );

      if (params.authStore.helpeePostMatchData?.needPhoneNumberVerification) {
        lastPhoneVerificationNumber.value = payload.phoneNumber;
        params.steps.goToStep('phone');
        params.phoneVerification.startTimer();
      } else if (params.optionalCriteria.value.length === 0) {
        lastPhoneVerificationNumber.value = null;
        await handleOptionalCriteriaSubmit();
      } else {
        lastPhoneVerificationNumber.value = null;
        params.steps.goToStep('criteria');
      }

      AnalyticsHelper.trackEvent('user_complete_information');
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.message?.includes('already exist')) {
        params.steps.goToStep('redirect');
      } else if (PHONE_NUMBER_ERROR_SUBTYPES.includes(data?.subtype)) {
        phoneError.value = localizeErrorMessage(
          findError(data.type as ErrorKeysType, data.subtype).message
        ) ?? null;
      }
    } finally {
      isLoading.value = false;
    }
  };

  const goToStepAfterPhoneVerification = async () => {
    lastPhoneVerificationNumber.value = null;

    if (params.optionalCriteria.value.length === 0) {
      await handleOptionalCriteriaSubmit();
      return;
    }

    params.steps.goToStep('criteria');
  };

  const handleResendCode = async () => {
    try {
      await params.phoneVerification.resendCode(async () => {
        if (!params.authStore.helpeePostSignUpData?.prospectUuid) {
          return;
        }

        await params.authStore.sendCodeRequest({
          phoneNumber: profileForm.phoneNumber,
          prospectUuid: params.authStore.helpeePostSignUpData.prospectUuid,
        });
      });
    } catch (error: any) {
      // verified meanwhile (e.g. the verify call went through but its response was lost)
      if (error?.response?.data?.subtype === 'PHONE-NUMBER-ALREADY-VERIFIED') {
        await goToStepAfterPhoneVerification();
        return;
      }
      throw error;
    }
  };

  const handleValidateCode = async () => {
    isLoading.value = true;

    const isValid = await params.phoneVerification.verifyCode(async () => {
      if (!params.authStore.helpeePostSignUpData?.prospectUuid) {
        return false;
      }

      await params.authStore.validateCode({
        code: verificationCode.value,
        prospectUuid: params.authStore.helpeePostSignUpData.prospectUuid,
      });

      return !params.authStore.codeValidated?.error;
    });

    isLoading.value = false;

    if (!isValid) {
      return;
    }

    await goToStepAfterPhoneVerification();
  };

  const handleOptionalCriteriaSubmit = async () => {
    const prospectUuid = params.authStore.helpeePostSignUpData?.prospectUuid;
    if (!prospectUuid) {
      return;
    }

    isLoading.value = true;

    try {
      const payload = buildCriteriaPayload(optionalCriteriaAnswers.value);

      await params.authStore.addOptionalInfoSignUpHelpee({
        prospectUuid,
        prospectCriteria: payload,
      });

      params.steps.goToStep('redirect');
      if (params.optionalCriteria.value.length > 0) {
        AnalyticsHelper.trackEvent('user_complete_details');
      }
    } finally {
      isLoading.value = false;
    }
  };

  const handleStrongCriteriaChange = async () => {
    await nextTick();
    AnalyticsHelper.trackEvent('first_criteria_filled');
  };

  const handleOptionalCriteriaChange = async (criteriaIndex: number) => {
    await nextTick();

    if (criteriaIndex === 0) {
      AnalyticsHelper.trackEvent('second_criteria_filled');
    } else if (criteriaIndex === 1) {
      AnalyticsHelper.trackEvent('third_criteria_filled');
    }
  };

  const updateVerificationCode = (value: string) => {
    verificationCode.value = value;
  };

  const updateOptionalCriteriaAnswers = (value: CriteriaAnswers) => {
    optionalCriteriaAnswers.value = value;
  };

  const goBack = () => {
    if (params.steps.isPhone.value) {
      params.phoneVerification.clearError();
      params.steps.goToStep('profile');
      return;
    }

    if (params.steps.isCriteria.value) {
      params.steps.goToStep('profile');
    }
  };

  const goBackToProfile = () => {
    params.phoneVerification.clearError();
    params.steps.goToStep('profile');
  };

  return {
    isLoading,
    phoneError,
    clearPhoneError,
    profileForm,
    verificationCode,
    optionalCriteriaAnswers,
    phoneRequired,
    hasOptionalSelection,
    handleIntroSuccess,
    handleProfileSubmit,
    handleResendCode,
    handleValidateCode,
    handleOptionalCriteriaSubmit,
    handleStrongCriteriaChange,
    handleOptionalCriteriaChange,
    updateVerificationCode,
    updateOptionalCriteriaAnswers,
    goBack,
    goBackToProfile,
  };
}
